// Startups table for the admin panel.
// Updated to use AdminStartupItem which includes enriched profile data (stage, mrr, industry, etc.)
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminStartupItem } from '../types/startup';
import { STAGE_LABELS, STAGE_COLORS } from '../types/startup';

const PLACEHOLDER = '—';

interface StartupsTableProps {
  startups: AdminStartupItem[];
  onViewClick: (startup: AdminStartupItem) => void;
}

const formatDate = (s: string | undefined): string => {
  if (!s) return PLACEHOLDER;
  const d = new Date(s);
  return isNaN(d.getTime()) ? PLACEHOLDER : d.toLocaleDateString();
};

const formatMrr = (mrr: number | null | undefined): string => {
  if (mrr === null || mrr === undefined) return PLACEHOLDER;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(mrr);
};

// "OwnerName (owner@email.com)" — falls back gracefully when name/email are missing
const formatOwner = (name: string | null | undefined, email: string | null | undefined): string => {
  if (!name && !email) return PLACEHOLDER;
  if (name && email) return `${name} (${email})`;
  return name ?? email ?? PLACEHOLDER;
};

const StartupsTable: React.FC<StartupsTableProps> = ({ startups, onViewClick }) => {
  const { t } = useTranslation('admin');

  return (
    <div className="overflow-x-auto">
      <table className="table table-zebra w-full">
        <thead>
          <tr>
            <th>{t('startups.table.name')}</th>
            <th>{t('startups.table.owner')}</th>
            <th>{t('startups.table.stage')}</th>
            <th>{t('startups.table.industry')}</th>
            <th>{t('startups.table.mrr')}</th>
            <th>{t('startups.table.updatedAt')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {startups.map((startup) => (
            <tr key={startup.id}>
              <td className="font-medium">{startup.name}</td>
              <td>{formatOwner(startup.ownerName, startup.ownerEmail)}</td>
              <td>
                {startup.stage ? (
                  <span
                    className={`badge badge-sm ${STAGE_COLORS[startup.stage] ?? 'badge-ghost'}`}
                  >
                    {STAGE_LABELS[startup.stage] ?? startup.stage}
                  </span>
                ) : (
                  PLACEHOLDER
                )}
              </td>
              <td>
                {startup.industry?.length ? startup.industry.slice(0, 2).join(', ') : PLACEHOLDER}
              </td>
              <td>{formatMrr(startup.mrr)}</td>
              <td>{formatDate(startup.updatedAt)}</td>
              <td>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => onViewClick(startup)}
                  aria-label={t('startups.actions.view')}
                >
                  <svg
                    width="8"
                    height="14"
                    viewBox="0 0 8 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.75 0.75L6.75 6.75L0.75 12.75"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StartupsTable;
